import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import {
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall, createApiUrl } from '../../config/api';

/**
 * Standalone printable per-truck daily schedule.
 * Opened via `/schedule/print?date=YYYY-MM-DD[&truck_id=N]` or
 * `?day_of_week=N&truck_id=N` (resolves to the next occurrence).
 *
 * The page has an on-screen toolbar that is hidden when printing.
 */
const TruckSchedulePrint = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  // Resolve the date from query string. If day_of_week is given but date
  // is not, resolve to the NEXT occurrence of that weekday (including today).
  // Uses local date math + manual YYYY-MM-DD formatting so results don't shift
  // across the UTC boundary on evening clicks in timezones west of UTC.
  const resolveDate = useCallback(() => {
    const toLocalIso = (d) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const params = new URLSearchParams(window.location.search);
    const explicitDate = params.get('date');
    if (explicitDate) return { date: explicitDate, truck_id: params.get('truck_id') };

    const dow = parseInt(params.get('day_of_week') || '', 10);
    const truck_id = params.get('truck_id');
    if (!Number.isNaN(dow) && dow >= 1 && dow <= 7) {
      const today = new Date();
      const jsTodayDow = today.getDay() === 0 ? 7 : today.getDay();
      const offset = (dow - jsTodayDow + 7) % 7;
      const target = new Date(today);
      target.setDate(today.getDate() + offset);
      return { date: toLocalIso(target), truck_id };
    }
    return { date: toLocalIso(new Date()), truck_id };
  }, []);

  const currentLang = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en';

  useEffect(() => {
    const { date, truck_id } = resolveDate();
    const qs = new URLSearchParams({ date, lang: currentLang });
    if (truck_id) qs.append('truck_id', truck_id);

    (async () => {
      try {
        setLoading(true);
        const response = await makeApiCall(`api/reports/truck-schedule?${qs.toString()}`);
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to load schedule');
        }
        const json = await response.json();
        setData(json);
      } catch (e) {
        console.error(e);
        setError(e.message || t('truckSchedule.failedToLoad'));
      } finally {
        setLoading(false);
      }
    })();
  }, [resolveDate, t, currentLang]);

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      const { date, truck_id } = resolveDate();
      const token = localStorage.getItem('token');
      const response = await fetch(createApiUrl('/api/reports/truck-schedule/pdf'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ date, truck_id: truck_id || undefined, lang: currentLang }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `schedule-${date}${truck_id ? '-truck-' + truck_id : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError(e.message || t('truckSchedule.failedToDownload'));
    } finally {
      setDownloading(false);
    }
  };

  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en-US';

  const formatDateLong = (iso) => {
    if (!iso) return '';
    // new Date('YYYY-MM-DD') is parsed as midnight UTC; format in UTC so the
    // weekday/day don't shift back a day in timezones west of UTC (e.g. EDT).
    const d = new Date(iso);
    return d.toLocaleDateString(locale, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      timeZone: 'UTC',
    });
  };

  // "1705 min (28:25)" — same format as the Route Setup time-allocation chip.
  const formatWorkMinutes = (minutes) => {
    const total = Math.round(Number(minutes) || 0);
    if (total <= 0) return '';
    const clock = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    return t('truckSchedule.workMinutes', { minutes: total, clock });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="schedule-print-root" sx={{ p: { xs: 1, sm: 2 } }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .schedule-print-root { padding: 0 !important; }
          .truck-section { page-break-inside: avoid; break-inside: avoid; }
          .truck-section + .truck-section { page-break-before: always; break-before: page; }
        }
        .print-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .print-table th, .print-table td { border-bottom: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
        .print-table th { background: #f0f0f0; border-bottom: 2px solid #333; }
        .print-table td.num, .print-table th.num { text-align: right; white-space: nowrap; }
        .truck-section { margin-bottom: 24px; }
        .truck-header { border-bottom: 2px solid #333; padding-bottom: 6px; margin-bottom: 8px; }
        .muted { color: #888; }
        .route-map-img { width: 100%; max-width: 100%; border: 1px solid #ccc; border-radius: 4px; display: block; margin: 8px 0 12px; }
        .route-map-missing { font-style: italic; color: #888; margin: 8px 0 12px; font-size: 12px; }
        .svc-list { margin: 0; padding-left: 16px; }
        .svc-list li { margin: 0 0 2px; }
      `}</style>

      <Stack direction="row" spacing={1} alignItems="center" className="no-print" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => window.close()}
          aria-label={t('common.close')}
        >
          {t('common.close')}
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          aria-label={t('common.print')}
        >
          {t('common.print')}
        </Button>
        <Button
          variant="outlined"
          startIcon={downloading ? <CircularProgress size={16} /> : <PdfIcon />}
          onClick={handleDownloadPdf}
          disabled={downloading}
          aria-label={t('truckSchedule.downloadPdf')}
        >
          {t('truckSchedule.downloadPdf')}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} className="no-print">
          {error}
        </Alert>
      )}

      <Typography variant="h4" sx={{ mb: 1 }}>
        {t('truckSchedule.title')}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        {formatDateLong(data?.date)}
      </Typography>

      {(!data || data.trucks?.length === 0) && (
        <Alert severity="info">{t('truckSchedule.noTrucks')}</Alert>
      )}

      {data?.trucks?.map((tr) => (
        <section key={tr.truck_id || 'unassigned'} className="truck-section">
          <div className="truck-header">
            <Typography variant="h6" sx={{ m: 0 }}>
              {tr.truck_name || t('truckSchedule.unassigned')}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5, fontSize: 13, flexWrap: 'wrap' }}>
              <span>
                <strong>{t('jobs.driver')}:</strong> {tr.driver || '—'}
              </span>
              <span>
                <strong>{t('jobs.passenger')}:</strong> {tr.passenger || '—'}
              </span>
              <span className="muted">
                {tr.stops.length} {t('truckSchedule.stops')}
              </span>
            </Stack>
          </div>
          {tr.map_image_url && tr.has_coords_for_map ? (
            <img
              src={tr.map_image_url}
              alt={t('truckSchedule.mapAlt')}
              className="route-map-img"
            />
          ) : (
            <div className="route-map-missing">
              {t('truckSchedule.mapUnavailable')}
            </div>
          )}
          <table className="print-table">
            <thead>
              <tr>
                <th className="num" style={{ width: '3%' }}>#</th>
                <th style={{ width: '20%' }}>{t('truckSchedule.customer')}</th>
                <th style={{ width: '24%' }}>{t('truckSchedule.address')}</th>
                <th style={{ width: '30%' }}>{t('truckSchedule.services')}</th>
                <th className="num" style={{ width: '9%' }}>{t('truckSchedule.workTime')}</th>
                <th style={{ width: '14%' }}>{t('truckSchedule.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {tr.stops.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ textAlign: 'center' }}>
                    {t('truckSchedule.noStops')}
                  </td>
                </tr>
              )}
              {tr.stops.map((s, idx) => {
                const customer = s.company_name
                  || `${s.first_name || ''} ${s.last_name || ''}`.trim()
                  || '—';
                const address = [s.street_address, s.city, s.province, s.postal_code]
                  .filter(Boolean).join(', ');
                return (
                  <tr key={s.id}>
                    <td className="num">{idx + 1}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{customer}</div>
                      {s.quote_number && (
                        <div className="muted" style={{ fontSize: 11 }}>
                          {s.quote_number}
                        </div>
                      )}
                    </td>
                    <td>{address}</td>
                    <td>
                      {Array.isArray(s.service_lines) && s.service_lines.length > 0 ? (
                        <ul className="svc-list">
                          {s.service_lines.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">
                      {formatWorkMinutes(s.work_minutes)}
                      {(s.work_breakdown || []).map((b, i) => (
                        <div key={i} className="muted" style={{ fontSize: 11 }}>
                          {t('truckSchedule.workBreakdownItem', { service: b.label, minutes: b.minutes })}
                        </div>
                      ))}
                    </td>
                    <td />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
    </Box>
  );
};

export default TruckSchedulePrint;
